(() => {
  'use strict';

  const CAROUSEL_SELECTOR = '[data-xcarousel]';
  const MOBILE_MEDIA_QUERY = window.matchMedia(
    '(max-width: 768px)'
  );

  class XCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(
        '.xcarousel__viewport'
      );
      this.track = root.querySelector(
        '.xcarousel__track'
      );
      this.slides = Array.from(
        root.querySelectorAll('.xcarousel__slide')
      );
      this.tabs = Array.from(
        root.querySelectorAll('.xcarousel__tab')
      );
      this.dots = Array.from(
        root.querySelectorAll('.xcarousel__dot')
      );

      this.index = 0;
      this.width = 0;

      this.startX = 0;
      this.currentX = 0;
      this.baseX = 0;
      this.activePointerId = null;

      this.dragging = false;
      this.moved = false;
      this.preventNextClick = false;

      this.dragThreshold = 14;
      this.startTime = 0;

      this.autoTimer = null;
      this.resizeTimer = null;
      this.resizeObserver = null;

      this.autoplaySpeed =
        Number(root.dataset.autoplaySpeed) || 3000;

      this.controller = new AbortController();
      this.signal = this.controller.signal;

      this.bind();
      this.update(false);
      this.updateTabs();
      this.updateDots();
      this.updateAutoplay();
    }

    bind() {
      this.root.addEventListener(
        'dragstart',
        this.preventNativeDrag,
        { signal: this.signal }
      );

      this.viewport?.addEventListener(
        'pointerdown',
        this.onDown,
        { signal: this.signal }
      );

      this.viewport?.addEventListener(
        'pointermove',
        this.onMove,
        { signal: this.signal }
      );

      this.viewport?.addEventListener(
        'pointerup',
        this.onUp,
        { signal: this.signal }
      );

      this.viewport?.addEventListener(
        'pointercancel',
        this.onCancel,
        { signal: this.signal }
      );

      this.viewport?.addEventListener(
        'lostpointercapture',
        this.onLostPointerCapture,
        { signal: this.signal }
      );

      /*
       * 如果刚刚发生过拖动，阻止图片链接或按钮误跳转。
       */
      this.root.addEventListener(
        'click',
        this.onClickCapture,
        {
          capture: true,
          signal: this.signal
        }
      );

      this.tabs.forEach((tab) => {
        tab.addEventListener(
          'mouseenter',
          this.onTabMouseEnter,
          { signal: this.signal }
        );

        tab.addEventListener(
          'focus',
          this.onTabFocus,
          { signal: this.signal }
        );
      });

      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          this.onDotClick,
          { signal: this.signal }
        );
      });

      window.addEventListener(
        'resize',
        this.onResize,
        {
          passive: true,
          signal: this.signal
        }
      );

      MOBILE_MEDIA_QUERY.addEventListener(
        'change',
        this.onMediaChange,
        { signal: this.signal }
      );

      document.addEventListener(
        'visibilitychange',
        this.onVisibilityChange,
        { signal: this.signal }
      );

      this.root.addEventListener(
        'mouseenter',
        this.onMouseEnter,
        { signal: this.signal }
      );

      this.root.addEventListener(
        'mouseleave',
        this.onMouseLeave,
        { signal: this.signal }
      );

      if ('ResizeObserver' in window && this.viewport) {
        this.resizeObserver = new ResizeObserver(() => {
          this.scheduleUpdate();
        });

        this.resizeObserver.observe(this.viewport);
      }

      this.root.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;

        image.addEventListener(
          'load',
          this.onImageLoad,
          {
            once: true,
            signal: this.signal
          }
        );
      });
    }

    preventNativeDrag = (event) => {
      event.preventDefault();
    };

    isMobile() {
      return MOBILE_MEDIA_QUERY.matches;
    }

    updateTabs() {
      if (!this.tabs.length) return;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === this.index;

        tab.classList.toggle('is-active', isActive);

        if (isActive) {
          tab.setAttribute('aria-current', 'true');
        } else {
          tab.removeAttribute('aria-current');
        }
      });
    }

    updateDots() {
      if (!this.dots.length) return;

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.index;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );
      });
    }

    update(animate = false) {
      if (!this.viewport || !this.track) return;

      this.width = this.viewport.getBoundingClientRect().width;

      if (!this.width) return;

      this.go(this.index, animate);
    }

    scheduleUpdate = () => {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.update(false);
      }, 100);
    };

    onResize = () => {
      this.scheduleUpdate();
    };

    onImageLoad = () => {
      this.scheduleUpdate();
    };

    onMediaChange = () => {
      this.update(false);
      this.updateAutoplay();
    };

    onVisibilityChange = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.updateAutoplay();
      }
    };

    onMouseEnter = () => {
      this.stopAutoplay();
    };

    onMouseLeave = () => {
      this.updateAutoplay();
    };

    /*
     * 鼠标悬停或键盘聚焦标签时，只切换轮播，
     * 点击标签仍然按照 href 正常进入集合页面。
     */
    onTabMouseEnter = (event) => {
      const tabIndex = Number(event.currentTarget.dataset.slide);

      if (Number.isNaN(tabIndex)) return;

      this.go(tabIndex, true);
    };

    onTabFocus = (event) => {
      const tabIndex = Number(event.currentTarget.dataset.slide);

      if (Number.isNaN(tabIndex)) return;

      this.go(tabIndex, true);
    };

    onDotClick = (event) => {
      const dotIndex = Number(
        event.currentTarget.dataset.dotIndex
      );

      if (Number.isNaN(dotIndex)) return;

      this.go(dotIndex, true);
      this.restartAutoplay();
    };

    onDown = (event) => {
      if (!this.isMobile()) return;

      /*
       * 点击文字按钮或活动内容时不开始轮播拖动。
       */
      if (
        event.target.closest(
          '.xcarousel__buttons, ' +
          '.xcarousel__floating-content, ' +
          '.xcarousel__tab, ' +
          '.xcarousel__dots'
        )
      ) {
        return;
      }

      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }

      this.stopAutoplay();

      this.dragging = true;
      this.moved = false;
      this.preventNextClick = false;

      this.activePointerId = event.pointerId;
      this.startX = event.clientX;
      this.baseX = -(this.index * this.width);
      this.currentX = this.baseX;
      this.startTime = performance.now();

      this.track.style.transition = 'none';
      this.root.classList.add('is-dragging');

      if (
        typeof this.viewport.setPointerCapture === 'function'
      ) {
        this.viewport.setPointerCapture(event.pointerId);
      }
    };

    onMove = (event) => {
      if (
        !this.dragging ||
        event.pointerId !== this.activePointerId
      ) {
        return;
      }

      const distance = event.clientX - this.startX;

      /*
       * 未超过阈值时不移动 UI，避免轻触产生位移。
       */
      if (
        !this.moved &&
        Math.abs(distance) < this.dragThreshold
      ) {
        return;
      }

      this.moved = true;
      this.currentX = this.baseX + distance;

      event.preventDefault();

      this.track.style.transform =
        `translate3d(${this.currentX}px, 0, 0)`;
    };

    onUp = (event) => {
      if (
        !this.dragging ||
        event.pointerId !== this.activePointerId
      ) {
        return;
      }

      const distance = event.clientX - this.startX;
      const elapsedTime =
        performance.now() - this.startTime;

      this.releasePointer(event.pointerId);

      this.dragging = false;
      this.activePointerId = null;
      this.root.classList.remove('is-dragging');

      const isClick =
        Math.abs(distance) < this.dragThreshold &&
        elapsedTime < 250;

      if (isClick) {
        this.go(this.index, true);
        this.restartAutoplay();
        return;
      }

      if (this.moved) {
        this.preventNextClick = true;

        if (distance < 0) {
          this.go(this.index + 1, true);
        } else {
          this.go(this.index - 1, true);
        }

        window.setTimeout(() => {
          this.preventNextClick = false;
        }, 0);
      } else {
        this.go(this.index, true);
      }

      this.moved = false;
      this.restartAutoplay();
    };

    onCancel = (event) => {
      if (
        !this.dragging ||
        event.pointerId !== this.activePointerId
      ) {
        return;
      }

      this.releasePointer(event.pointerId);

      this.dragging = false;
      this.moved = false;
      this.activePointerId = null;

      this.root.classList.remove('is-dragging');
      this.go(this.index, true);
      this.restartAutoplay();
    };

    onLostPointerCapture = () => {
      if (!this.dragging) return;

      this.dragging = false;
      this.moved = false;
      this.activePointerId = null;

      this.root.classList.remove('is-dragging');
      this.go(this.index, true);
      this.restartAutoplay();
    };

    releasePointer(pointerId) {
      if (
        typeof this.viewport?.hasPointerCapture === 'function' &&
        this.viewport.hasPointerCapture(pointerId)
      ) {
        this.viewport.releasePointerCapture(pointerId);
      }
    }

    onClickCapture = (event) => {
      if (!this.preventNextClick) return;

      event.preventDefault();
      event.stopPropagation();
    };

    go(index, animate = true, autoplay = false) {
      if (!this.slides.length || !this.track) return;

      const maximumIndex = this.slides.length - 1;

      this.index = Math.max(
        0,
        Math.min(index, maximumIndex)
      );

      const translateX = -(this.index * this.width);

      if (!animate) {
        this.track.style.transition = 'none';
      } else {
        this.track.style.transition = autoplay
          ? 'transform 1.5s cubic-bezier(0.22, 1, 0.36, 1)'
          : 'transform 1s cubic-bezier(0.22, 1, 0.36, 1)';
      }

      this.track.style.transform =
        `translate3d(${translateX}px, 0, 0)`;

      this.currentX = translateX;
      this.updateTabs();
      this.updateDots();
    }

    startAutoplay() {
      if (
        document.hidden ||
        this.slides.length <= 1
      ) {
        return;
      }

      this.stopAutoplay();

      this.autoTimer = window.setInterval(() => {
        const nextIndex =
          this.index >= this.slides.length - 1
            ? 0
            : this.index + 1;

        this.go(nextIndex, true, true);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoTimer) return;

      window.clearInterval(this.autoTimer);
      this.autoTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    updateAutoplay() {
      if (!document.hidden) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    destroy() {
      this.stopAutoplay();
      window.clearTimeout(this.resizeTimer);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      this.controller.abort();

      if (this.track) {
        this.track.style.transition = '';
        this.track.style.transform = '';
      }

      this.root.classList.remove('is-dragging');
      this.root.removeAttribute('data-xcarousel-initialized');

      delete this.root.xCarouselInstance;
    }
  }

  function initXCarousel(root = document) {
    const carousels = [];

    if (root.matches?.(CAROUSEL_SELECTOR)) {
      carousels.push(root);
    }

    root
      .querySelectorAll?.(CAROUSEL_SELECTOR)
      .forEach((carousel) => {
        carousels.push(carousel);
      });

    carousels.forEach((carousel) => {
      if (
        carousel.dataset.xcarouselInitialized === 'true'
      ) {
        return;
      }

      carousel.dataset.xcarouselInitialized = 'true';
      carousel.xCarouselInstance = new XCarousel(carousel);
    });
  }

  function destroyXCarousel(root) {
    const carousels = [];

    if (root.matches?.(CAROUSEL_SELECTOR)) {
      carousels.push(root);
    }

    root
      .querySelectorAll?.(CAROUSEL_SELECTOR)
      .forEach((carousel) => {
        carousels.push(carousel);
      });

    carousels.forEach((carousel) => {
      if (
        carousel.xCarouselInstance &&
        typeof carousel.xCarouselInstance.destroy === 'function'
      ) {
        carousel.xCarouselInstance.destroy();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initXCarousel(document),
      { once: true }
    );
  } else {
    initXCarousel(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initXCarousel(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyXCarousel(event.target);
    }
  );
})();